import React from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import './RollerTrailersPage.css'

interface TrailerModel {
  model: string;
  capacity: number;
  boatLength: string;
  overallLength: string;
  bowEyeMin: string;
  bowEyeMax: string;
  gvwr: number;
  tongueJack: string;
  brakes: string;
  rollers: number;
  tireSize: string;
  overallWidth: string;
}

const RollerTrailersPage: React.FC = () => {
  // Aluminum Single Axle Roller Trailers
  const aluminumSingleAxleModels: TrailerModel[] = [
    {
      model: "VAR-2425",
      capacity: 2425,
      boatLength: "16'-18'",
      overallLength: "19'10\"",
      bowEyeMin: "14'",
      bowEyeMax: "18'",
      gvwr: 2995,
      tongueJack: "Optional",
      brakes: "Optional",
      rollers: 16,
      tireSize: "ST185/80R 13 LRD 5H",
      overallWidth: "89\"",
    },
    {
      model: "VAR-2625",
      capacity: 2625,
      boatLength: "17'-19'",
      overallLength: "19'10\"",
      bowEyeMin: "14'",
      bowEyeMax: "18'",
      gvwr: 3185,
      tongueJack: "Optional",
      brakes: "Optional",
      rollers: 20,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "89\"",
    },
    {
      model: "VAR-2825",
      capacity: 2825,
      boatLength: "18'-20'",
      overallLength: "23'2\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 3445,
      tongueJack: "Optional",
      brakes: "Optional",
      rollers: 20,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VAR-3025",
      capacity: 3025,
      boatLength: "18'-20'",
      overallLength: "23'2\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 3775,
      tongueJack: "Single Wheel",
      brakes: "Disc",
      rollers: 20,
      tireSize: "ST215/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VAR-3325",
      capacity: 3325,
      boatLength: "20'-22'",
      overallLength: "24'1\"",
      bowEyeMin: "17'6\"",
      bowEyeMax: "21'",
      gvwr: 4150,
      tongueJack: "Single Wheel",
      brakes: "Disc",
      rollers: 20,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    },
    {
      model: "VAR-3525",
      capacity: 3525,
      boatLength: "20'-22'",
      overallLength: "24'1\"",
      bowEyeMin: "17'6\"",
      bowEyeMax: "21'",
      gvwr: 4350,
      tongueJack: "Single Wheel",
      brakes: "Disc",
      rollers: 20,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    }
  ];

  // Aluminum Tandem Axle Roller Trailers
  const aluminumTandemAxleModels: TrailerModel[] = [
    {
      model: "VATR-3625",
      capacity: 3625,
      boatLength: "18'-20'",
      overallLength: "23'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 4575,
      tongueJack: "Single Wheel",
      brakes: "Disc-1",
      rollers: 20,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VATR-4225",
      capacity: 4225,
      boatLength: "21'-22'",
      overallLength: "24'1\"",
      bowEyeMin: "17'6\"",
      bowEyeMax: "21'",
      gvwr: 5275,
      tongueJack: "Single Wheel",
      brakes: "Disc-1",
      rollers: 24,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VATR-5225",
      capacity: 5225,
      boatLength: "21'-22'",
      overallLength: "24'1\"",
      bowEyeMin: "17'6\"",
      bowEyeMax: "21'",
      gvwr: 6275,
      tongueJack: "Single Wheel",
      brakes: "Disc-1",
      rollers: 28,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VATR-6025",
      capacity: 6025,
      boatLength: "23'-24'",
      overallLength: "25'9\"",
      bowEyeMin: "18'10\"",
      bowEyeMax: "23'",
      gvwr: 7125,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-1",
      rollers: 44,
      tireSize: "ST205/75R 15 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VATR-6425",
      capacity: 6425,
      boatLength: "24'-25'",
      overallLength: "27'7\"",
      bowEyeMin: "19'",
      bowEyeMax: "24'",
      gvwr: 7755,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-1",
      rollers: 44,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    }
  ];

  // Galvanized Single Axle Roller Trailers
  const galvanizedSingleAxleModels: TrailerModel[] = [
    {
      model: "VR-1550",
      capacity: 1550,
      boatLength: "14'-15'",
      overallLength: "16'5\"",
      bowEyeMin: "10'6\"",
      bowEyeMax: "14'6\"",
      gvwr: 1950,
      tongueJack: "Optional",
      brakes: "No",
      rollers: 12,
      tireSize: "5.30 x 12 LRC 5H",
      overallWidth: "78\"",
    },
    {
      model: "VR-1850",
      capacity: 1850,
      boatLength: "15'-16'",
      overallLength: "19'3\"",
      bowEyeMin: "10'6\"",
      bowEyeMax: "16'6\"",
      gvwr: 2250,
      tongueJack: "Optional",
      brakes: "No",
      rollers: 12,
      tireSize: "5.30 x 12 LRD 5H",
      overallWidth: "78\"",
    },
    {
      model: "VR-2650",
      capacity: 2650,
      boatLength: "18'-19'",
      overallLength: "22'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "18'",
      gvwr: 3500,
      tongueJack: "Optional",
      brakes: "Optional",
      rollers: 20,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VR-3050S",
      capacity: 3050,
      boatLength: "18'-20'",
      overallLength: "22'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "18'",
      gvwr: 3850,
      tongueJack: "Single Wheel",
      brakes: "Disc",
      rollers: 20,
      tireSize: "ST215/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VR-3350",
      capacity: 3350,
      boatLength: "20'-21'",
      overallLength: "23'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "20'",
      gvwr: 4275,
      tongueJack: "Single Wheel",
      brakes: "Disc",
      rollers: 20,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    }
  ];

  // Galvanized Tandem Axle Roller Trailers  
  const galvanizedTandemAxleModels: TrailerModel[] = [
    {
      model: "VRT-3650S",
      capacity: 3650,
      boatLength: "18'-20'",
      overallLength: "22'6\"",
      bowEyeMin: "16'",
      bowEyeMax: "19'",
      gvwr: 4650,
      tongueJack: "Single Wheel",
      brakes: "Disc-1",
      rollers: 20,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VRT-4750",
      capacity: 4750,
      boatLength: "21'-23'",
      overallLength: "24'9\"",
      bowEyeMin: "17'",
      bowEyeMax: "21'",
      gvwr: 5900,
      tongueJack: "Single Wheel",
      brakes: "Disc-1",
      rollers: 28,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VRT-5950",
      capacity: 5950,
      boatLength: "24'-25'",
      overallLength: "27'6\"",
      bowEyeMin: "19'",
      bowEyeMax: "24'",
      gvwr: 7450,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-1",
      rollers: 44,
      tireSize: "ST215/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VRT-7950",
      capacity: 7950,
      boatLength: "25'-27'",
      overallLength: "28'5\"",
      bowEyeMin: "20'",
      bowEyeMax: "25'",
      gvwr: 9550,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-1",
      rollers: 64,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    }
  ];

  // Galvanized Triple Axle Roller Trailers
  const galvanizedTripleAxleModels: TrailerModel[] = [
    {
      model: "VRT-8650",
      capacity: 8650,
      boatLength: "27'-28'",
      overallLength: "31'6\"",
      bowEyeMin: "22'",
      bowEyeMax: "27'6\"",
      gvwr: 10450,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-2",
      rollers: 64,
      tireSize: "ST205/75R 14 LRC 5H",
      overallWidth: "100\"",
    },
    {
      model: "VRT-9250",
      capacity: 9250,
      boatLength: "28'-30'",
      overallLength: "32'11\"",
      bowEyeMin: "22'",
      bowEyeMax: "29'",
      gvwr: 11050,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-2",
      rollers: 64,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    },
    {
      model: "VRT-10850",
      capacity: 10850,
      boatLength: "29'-31'",
      overallLength: "32'11\"",
      bowEyeMin: "22'",
      bowEyeMax: "29'",
      gvwr: 12750,
      tongueJack: "2000lb. Foot",
      brakes: "Disc-2",
      rollers: 64,
      tireSize: "ST225/75R 15 LRD 6H",
      overallWidth: "100\"",
    }
  ];

  const rollerTrailerFeatures = [
    "Plug & Play Lights",
    "Aluminum Diamond Plate Fenders", 
    "Heavy Duty Winch Straps",
    "D.O.T. Composite Brake Line with Brass Fittings",
    "Balanced Radial Tires",
    "Eliminator GalvX Vented Rotor Disc Brakes",
    "Super Lube Spindles",
    "Wheel Balancing",
    "Galvanized Hardware, U-bolts, Winch Stand, Axles, Tongue",
    "Articulating roller system for easy launching",
    "Corrosion-resistant roller assemblies",
    "Adjustable roller positions for perfect boat fit"
  ];

  return (
    <div className="roller-page">
      <SEOHead
        title="Roller Boat Trailers - Easy Launch & Retrieve | Fast Boat Trailers"
        description="Roller boat trailers from Fast Boat Trailers in Edgewater, MD. Aluminum and galvanized roller trailers for shallow water launching. Easy single-person boat retrieval with articulating roller systems."
        keywords="roller boat trailers, roller trailers Maryland, aluminum roller trailers, shallow water boat trailers, easy launch trailers, articulating roller system"
        ogTitle="Roller Boat Trailers - Easy Launch & Retrieve"
        ogDescription="Roller boat trailers perfect for shallow water launching. Aluminum and galvanized options with articulating roller systems for easy boat retrieval."
        ogImage="/images/trailers/roller-aluminum-single-axle.jpg"
        canonical="https://fastboattrailers.com/trailers/roller"
      />
      <div className="container">
        {/* Hero Section */}
        <div className="roller-hero">
          <div className="hero-main">
            <div className="hero-content">
              <h1>Roller Trailers</h1>
              <h2>Perfect for Shallow Ramps & Low Tide</h2>
              <p>
                Venture Roller Trailers are the best option for boaters who plan to launch and load at shallow ramps or low tide. 
                Because roller trailers don't need to be submerged as deep as bunk trailers, the running gear isn't frequently exposed to salt or freshwater. 
                This means less damage over time. We offer both hot dipped galvanized steel and aluminum roller trailers to fit your boating needs.
              </p>
              
              <h3>Benefits of Roller Trailers</h3>
              <ul className="benefits-list">
                <li>Great for low tide or shallow ramps</li>
                <li>Easy launching and loading with articulating roller system</li>
                <li>Less exposure to water means longer lasting components</li>
                <li>Smooth boat loading with multiple roller contact points</li>
              </ul>
            </div>
            <div className="hero-features">
              <h3>Standard Features</h3>
              <div className="features-compact">
                {rollerTrailerFeatures.map((feature, index) => (
                  <div key={index} className="feature-item-compact">
                    <div className="feature-icon-compact">✓</div>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Aluminum Roller Trailers Section */}
        <div className="models-section">
          <h2>Aluminum Roller Trailers</h2>
          
          {/* Aluminum Single Axle */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-aluminum-single-axle.jpg" alt="Aluminum Single Axle Roller Trailers" />
            </div>
            <div className="category-header">
              <h3>Aluminum Single Axle Roller Trailers</h3>
              <p>Lightweight and corrosion-resistant aluminum construction with single axle design. Perfect for smaller boats with capacity ranges from 2,425 to 3,525 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Rollers</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumSingleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.rollers}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Aluminum Tandem Axle */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-aluminum-tandem-axle.jpg" alt="Aluminum Tandem Axle Roller Trailers" />
            </div>
            <div className="category-header">
              <h3>Aluminum Tandem Axle Roller Trailers</h3>
              <p>Heavy-duty aluminum tandem axle design for larger boats. Superior load distribution and stability with capacity ranges from 3,625 to 6,425 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Rollers</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {aluminumTandemAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.rollers}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Galvanized Roller Trailers Section */}
        <div className="models-section">
          <h2>Galvanized Roller Trailers</h2>
          
          {/* Galvanized Single Axle */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-galvanized-single-axle.jpg" alt="Galvanized Single Axle Roller Trailers" />
            </div>
            <div className="category-header">
              <h3>Galvanized Single Axle Roller Trailers</h3>
              <p>Hot-dipped galvanized steel construction provides maximum corrosion protection. Single axle design for smaller to medium boats with capacity ranges from 1,550 to 3,350 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Rollers</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedSingleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.rollers}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Galvanized Tandem Axle */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-galvanized-tandem-axle.jpg" alt="Galvanized Tandem Axle Roller Trailers" />
            </div>
            <div className="category-header">
              <h3>Galvanized Tandem Axle Roller Trailers</h3>
              <p>Heavy-duty galvanized tandem axle construction for larger boats. Superior load distribution with capacity ranges from 3,650 to 7,950 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Rollers</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedTandemAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.rollers}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Galvanized Triple Axle */}
          <div className="model-category">
            <div className="category-image-full">
              <img src="/images/trailers/roller-galvanized-triple-axle.jpg" alt="Galvanized Triple Axle Roller Trailers" />
            </div>
            <div className="category-header">
              <h3>Galvanized Triple Axle Roller Trailers</h3>
              <p>Maximum capacity galvanized triple axle design for the largest boats. Ultimate load distribution and stability with capacity ranges from 8,650 to 10,850 lbs.</p>
            </div>
            <div className="models-table-container">
              <table className="models-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Capacity (lbs)</th>
                    <th>Boat Length</th>
                    <th>Overall Length</th>
                    <th>Bow Eye Min</th>
                    <th>Bow Eye Max</th>
                    <th>GVWR</th>
                    <th>Tongue Jack</th>
                    <th>Brakes</th>
                    <th>Rollers</th>
                    <th>Tire Size</th>
                    <th>Overall Width</th>
                  </tr>
                </thead>
                <tbody>
                  {galvanizedTripleAxleModels.map((model, index) => (
                    <tr key={index}>
                      <td className="model-name">{model.model}</td>
                      <td>{model.capacity.toLocaleString()}</td>
                      <td>{model.boatLength}</td>
                      <td>{model.overallLength}</td>
                      <td>{model.bowEyeMin}</td>
                      <td>{model.bowEyeMax}</td>
                      <td>{model.gvwr.toLocaleString()}</td>
                      <td>{model.tongueJack}</td>
                      <td>{model.brakes}</td>
                      <td>{model.rollers}</td>
                      <td>{model.tireSize}</td>
                      <td>{model.overallWidth}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="roller-cta">
          <h2>Ready to Get Your Roller Trailer?</h2>
          <p>
            Find the perfect roller trailer for your shallow water launching needs. Our aluminum and galvanized options 
            provide the durability and performance you need for years of reliable boating.
          </p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn btn-primary">Get a quote</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RollerTrailersPage;